import { Block, BlockType, CommodityBlock, UpdateBlock } from '@features/create/types';
import { Commodity } from 'helpers/fakeData';
import { uuidv4 } from 'helpers/uuid';
import { keyBy } from 'lodash';
import { useState } from 'react';
import { createStore, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';

type Actions = {
  addBlock: () => void;
  removeBlock: (id: string) => void;
  updateBlock: (id: string, update: UpdateBlock) => void;
};

type TreeState = {
  blocksById: Record<string, Block>;
  actions: Actions;
};

interface UseTreeStateOptions {
  initialBlockCount?: number;
  initialCommodity?: Commodity;
}

export function useTreeState(options: UseTreeStateOptions = {}) {
  const { initialBlockCount = 1, initialCommodity } = options;
  const intialBlocks: CommodityBlock[] = Array.from({ length: initialBlockCount }, () => ({
    id: uuidv4(),
    type: BlockType.Commodity as const,
    commodity: initialCommodity,
  }));

  const [store] = useState(() =>
    createStore<TreeState>()(
      devtools(
        immer((set) => {
          return {
            blocksById: keyBy(intialBlocks, 'id'),
            actions: {
              addBlock: () => {
                set((state) => {
                  const id = uuidv4();
                  state.blocksById[id] = { id, type: BlockType.Commodity };
                });
              },
              removeBlock: (id: string) => {
                set((state) => {
                  delete state.blocksById[id];
                });
              },
              updateBlock: (id: string, update: UpdateBlock) => {
                set((state) => {
                  const block = state.blocksById[id];
                  if (block == null) {
                    return;
                  }
                  const { commodity } = update;
                  state.blocksById[id] = { ...block, commodity };
                });
              },
            },
          };
        }),
        {
          name: 'TreeStore',
        },
      ),
    ),
  );

  const blocks = useStore(
    store,
    useShallow((state) => Object.values(state.blocksById)),
  );
  const addBlock = useStore(store, (state) => state.actions.addBlock);
  const removeBlock = useStore(store, (state) => state.actions.removeBlock);
  const updateBlock = useStore(store, (state) => state.actions.updateBlock);

  return { blocks, addBlock, removeBlock, updateBlock };
}
